/**
 * AxLabs Mathematics Experiments - Number Systems
 *
 * Collection of experiments exploring number systems and their properties.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, isPrime, countPrimeFactors, gcd, lcm, factorial } from './helpers.js';
// ============================================================================
// PRIME DISTRIBUTION EXPLORER
// ============================================================================
export const PrimeDistributionExplorer = createExperimentFromSchema({
    id: 'prime-distribution-explorer',
    name: 'Prime Distribution Explorer',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Investigate the distribution and density of prime numbers within a given interval through empirical observation and comparison with theoretical approximations.',
    conceptTags: ['Prime Numbers', 'Number Theory', 'Asymptotic Analysis', 'Prime Number Theorem'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'n', name: 'Number', unit: 'dimensionless', initial: 2, symbol: 'n', description: 'Current number being checked' },
        { id: 'primeCount', name: 'Prime Count', unit: 'dimensionless', initial: 0, symbol: '\\pi(n)', description: 'Number of primes found up to n' },
        { id: 'liApprox', name: 'Li Approximation', unit: 'dimensionless', initial: 0, symbol: 'Li(n)', description: 'Logarithmic integral approximation' },
        { id: 'nLogN', name: 'n/ln(n)', unit: 'dimensionless', initial: 0, symbol: 'n/\\ln(n)', description: 'Simple prime number theorem approximation' },
        { id: 'gap', name: 'Prime Gap', unit: 'dimensionless', initial: 0, symbol: 'g_n', description: 'Distance to previous prime' },
        { id: 'lastPrime', name: 'Last Prime', unit: 'dimensionless', initial: 2, symbol: 'p_{last}' },
    ],
    parameters: [
        { id: 'maxN', name: 'Maximum Number', unit: 'dimensionless', min: 10, max: 1000, default: 100, symbol: 'N_{max}' },
        { id: 'stepSize', name: 'Step Size', unit: 'dimensionless', min: 1, max: 10, default: 1, symbol: '\\Delta' },
    ],
    equations: [
        {
            id: 'prime-theorem',
            name: 'Prime Number Theorem',
            latex: '\\pi(n) \\sim \\frac{n}{\\ln n}',
            description: 'The number of primes less than n is asymptotically equivalent to n/ln(n)',
        },
        {
            id: 'li-approximation',
            name: 'Logarithmic Integral',
            latex: 'Li(n) = \\int_2^n \\frac{dt}{\\ln t}',
            description: 'Better approximation to prime counting function',
        },
    ],
    derivatives: (state, params) => {
        const n = getState(state, 'n');
        const maxN = getParam(params, 'maxN');
        const stepSize = getParam(params, 'stepSize');
        const primeCount = getState(state, 'primeCount');
        const lastPrime = getState(state, 'lastPrime');
        const nextN = Math.min(n + stepSize, maxN);
        let newPrimeCount = primeCount;
        let newLastPrime = lastPrime;
        const gap = isPrime(nextN) ? nextN - lastPrime : getState(state, 'gap');
        if (isPrime(nextN)) {
            newPrimeCount = primeCount + 1;
            newLastPrime = nextN;
        }
        const liApprox = nextN / Math.log(Math.max(nextN, 2));
        const nLogN = nextN / Math.log(Math.max(nextN, 2));
        return {
            n: nextN - n,
            primeCount: newPrimeCount - primeCount,
            liApprox: liApprox - getState(state, 'liApprox'),
            nLogN: nLogN - getState(state, 'nLogN'),
            gap: isPrime(nextN) ? gap - getState(state, 'gap') : 0,
            lastPrime: newLastPrime - lastPrime,
        };
    },
    bounds: {
        n: { min: 2, max: 10000 },
        primeCount: { min: 0, max: 2000 },
    },
});
// ============================================================================
// DIVISIBILITY PATTERN INVESTIGATOR
// ============================================================================
export const DivisibilityPatternInvestigator = createExperimentFromSchema({
    id: 'divisibility-pattern-investigator',
    name: 'Divisibility Pattern Investigator',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Explore divisibility rules and patterns across varying bases through systematic investigation of factor structures and relationships.',
    conceptTags: ['Divisibility', 'Modular Arithmetic', 'Number Theory', 'Factors'],
    difficulty: 'beginner',
    stateVariables: [
        { id: 'n', name: 'Number', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'factorCount', name: 'Factor Count', unit: 'dimensionless', initial: 1, symbol: 'd(n)', description: 'Number of divisors' },
        { id: 'sumFactors', name: 'Sum of Factors', unit: 'dimensionless', initial: 1, symbol: '\\sigma(n)' },
        { id: 'primeFactors', name: 'Prime Factor Count', unit: 'dimensionless', initial: 0, symbol: '\\Omega(n)' },
        { id: 'isComposite', name: 'Is Composite', unit: 'dimensionless', initial: 0, symbol: '\\mathbb{C}' },
    ],
    parameters: [
        { id: 'maxN', name: 'Maximum Number', unit: 'dimensionless', min: 10, max: 500, default: 100, symbol: 'N_{max}' },
        { id: 'step', name: 'Step Size', unit: 'dimensionless', min: 1, max: 5, default: 1, symbol: '\\Delta' },
    ],
    equations: [
        {
            id: 'divisor-function',
            name: 'Divisor Function',
            latex: 'd(n) = \\sum_{d|n} 1',
            description: 'Counts the number of positive divisors of n',
        },
        {
            id: 'sigma-function',
            name: 'Sigma Function',
            latex: '\\sigma(n) = \\sum_{d|n} d',
            description: 'Sum of all positive divisors of n',
        },
    ],
    derivatives: (state, params) => {
        const n = getState(state, 'n');
        const maxN = getParam(params, 'maxN');
        const step = getParam(params, 'step');
        const nextN = Math.min(n + step, maxN);
        let factorCount = 0;
        let sumFactors = 0;
        for (let i = 1; i <= Math.abs(nextN); i++) {
            if (nextN % i === 0) {
                factorCount++;
                sumFactors += i;
            }
        }
        const primeFactors = countPrimeFactors(nextN);
        const isComposite = factorCount > 2 && nextN > 1 ? 1 : 0;
        return {
            n: nextN - n,
            factorCount: factorCount - getState(state, 'factorCount'),
            sumFactors: sumFactors - getState(state, 'sumFactors'),
            primeFactors: primeFactors - getState(state, 'primeFactors'),
            isComposite: isComposite - getState(state, 'isComposite'),
        };
    },
    bounds: {
        n: { min: 1, max: 10000 },
        factorCount: { min: 1, max: 1000 },
    },
});
// ============================================================================
// GCD/LCM RELATIONSHIP LAB
// ============================================================================
export const GCDLCMRelationshipLab = createExperimentFromSchema({
    id: 'gcd-lcm-relationship-lab',
    name: 'GCD/LCM Relationship Lab',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Empirically verify the fundamental relationship between greatest common divisor and least common multiple: gcd(a,b) × lcm(a,b) = a × b.',
    conceptTags: ['GCD', 'LCM', 'Number Theory', 'Euclidean Algorithm'],
    difficulty: 'beginner',
    stateVariables: [
        { id: 'a', name: 'First Number', unit: 'dimensionless', initial: 12, symbol: 'a' },
        { id: 'b', name: 'Second Number', unit: 'dimensionless', initial: 18, symbol: 'b' },
        { id: 'g', name: 'GCD', unit: 'dimensionless', initial: 6, symbol: '\\gcd(a,b)' },
        { id: 'l', name: 'LCM', unit: 'dimensionless', initial: 36, symbol: '\\operatorname{lcm}(a,b)' },
        { id: 'product', name: 'Product', unit: 'dimensionless', initial: 216, symbol: 'ab' },
        { id: 'gcdLcmProduct', name: 'GCD × LCM', unit: 'dimensionless', initial: 216, symbol: '\\gcd \\cdot \\operatorname{lcm}' },
    ],
    parameters: [
        { id: 'a_target', name: 'A Target', unit: 'dimensionless', min: 1, max: 100, default: 12, symbol: 'a' },
        { id: 'b_target', name: 'B Target', unit: 'dimensionless', min: 1, max: 100, default: 18, symbol: 'b' },
        { id: 'rateA', name: 'Rate of Change A', unit: 'dimensionless/s', min: -5, max: 5, default: 0.5, symbol: 'r_a' },
        { id: 'rateB', name: 'Rate of Change B', unit: 'dimensionless/s', min: -5, max: 5, default: 0.3, symbol: 'r_b' },
    ],
    equations: [
        {
            id: 'gcd-lcm-theorem',
            name: 'GCD-LCM Theorem',
            latex: '\\gcd(a,b) \\cdot \\operatorname{lcm}(a,b) = |ab|',
            description: 'The product of GCD and LCM equals the product of the numbers',
        },
        {
            id: 'euclidean-algorithm',
            name: 'Euclidean Algorithm',
            latex: '\\gcd(a,b) = \\gcd(b, a \\mod b)',
            description: 'Recursive algorithm for finding GCD',
        },
    ],
    derivatives: (state, params) => {
        const rateA = getParam(params, 'rateA');
        const rateB = getParam(params, 'rateB');
        const da = rateA;
        const db = rateB;
        const currentA = getState(state, 'a');
        const currentB = getState(state, 'b');
        const nextA = Math.max(1, Math.min(100, currentA + da * 0.016));
        const nextB = Math.max(1, Math.min(100, currentB + db * 0.016));
        const g = gcd(nextA, nextB);
        const l = lcm(nextA, nextB);
        return {
            a: nextA - currentA,
            b: nextB - currentB,
            g: g - getState(state, 'g'),
            l: l - getState(state, 'l'),
            product: nextA * nextB - getState(state, 'product'),
            gcdLcmProduct: g * l - getState(state, 'gcdLcmProduct'),
        };
    },
    bounds: {
        a: { min: 1, max: 100 },
        b: { min: 1, max: 100 },
    },
});
// ============================================================================
// MODULAR ARITHMETIC CYCLES
// ============================================================================
export const ModularArithmeticCycles = createExperimentFromSchema({
    id: 'modular-arithmetic-cycles',
    name: 'Modular Arithmetic Cycles',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Study cyclic behavior and patterns in modular arithmetic through exploration of powers, multiplication, and additive sequences.',
    conceptTags: ['Modular Arithmetic', 'Number Theory', 'Cyclic Groups', 'Euler\'s Theorem'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'n', name: 'Current Number', unit: 'dimensionless', initial: 1, symbol: 'n_t' },
        { id: 'remainder', name: 'Remainder', unit: 'dimensionless', initial: 1, symbol: 'n_t \\mod m', description: 'Value modulo m' },
        { id: 'power', name: 'Power', unit: 'dimensionless', initial: 1, symbol: 'a^t', description: 'Current power of base' },
    ],
    parameters: [
        { id: 'base', name: 'Base', unit: 'dimensionless', min: 2, max: 20, default: 2, symbol: 'a' },
        { id: 'modulus', name: 'Modulus', unit: 'dimensionless', min: 2, max: 50, default: 7, symbol: 'm' },
        { id: 'multiplier', name: 'Multiplier', unit: 'dimensionless', min: 1, max: 10, default: 2, symbol: 'k' },
    ],
    equations: [
        {
            id: 'mod-property',
            name: 'Modular Exponentiation',
            latex: 'a^t \\mod m',
            description: 'Compute the remainder when a^t is divided by m',
        },
        {
            id: 'euler-theorem',
            name: 'Euler\'s Theorem',
            latex: 'a^{\\phi(m)} \\equiv 1 \\pmod m',
            description: 'If a and m are coprime, then a^φ(m) ≡ 1 (mod m)',
        },
    ],
    derivatives: (state, params) => {
        const base = getParam(params, 'base');
        const modulus = getParam(params, 'modulus');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        const power = Math.pow(base, Math.floor(nextT));
        const remainder = ((power % modulus) + modulus) % modulus;
        return {
            t: 0.016,
            n: power - getState(state, 'n'),
            remainder: remainder - getState(state, 'remainder'),
            power: power - getState(state, 'power'),
        };
    },
    bounds: {
        t: { min: 0, max: 60 },
        n: { min: 1, max: 1e10 },
    },
});
// ============================================================================
// DECIMAL EXPANSION BEHAVIOR
// ============================================================================
export const DecimalExpansionBehavior = createExperimentFromSchema({
    id: 'decimal-expansion-behavior',
    name: 'Decimal Expansion Behavior',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Explore rational number decimal patterns, investigating the distinction between terminating and repeating decimals and their relationship to denominator structure.',
    conceptTags: ['Fractions', 'Decimal Expansion', 'Repeating Decimals', 'Number Theory'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'numerator', name: 'Numerator', unit: 'dimensionless', initial: 1, symbol: 'p' },
        { id: 'denominator', name: 'Denominator', unit: 'dimensionless', initial: 3, symbol: 'q' },
        { id: 'decimal', name: 'Decimal Value', unit: 'dimensionless', initial: 0.333, symbol: 'p/q' },
        { id: 'period', name: 'Repeating Period', unit: 'dimensionless', initial: 1, symbol: 'T', description: 'Length of repeating cycle' },
        { id: 'isTerminating', name: 'Is Terminating', unit: 'dimensionless', initial: 0, symbol: '\\tau' },
    ],
    parameters: [
        { id: 'p_target', name: 'Numerator', unit: 'dimensionless', min: 1, max: 50, default: 1, symbol: 'p' },
        { id: 'q_target', name: 'Denominator', unit: 'dimensionless', min: 2, max: 100, default: 3, symbol: 'q' },
        { id: 'rateP', name: 'Rate P', unit: 'dimensionless/s', min: -2, max: 2, default: 0.1, symbol: 'r_p' },
        { id: 'rateQ', name: 'Rate Q', unit: 'dimensionless/s', min: -2, max: 2, default: 0.05, symbol: 'r_q' },
    ],
    equations: [
        {
            id: 'decimal-expansion',
            name: 'Decimal Expansion',
            latex: '\\frac{p}{q} = a_0.a_1a_2\\ldots\\overline{a_{k+1}\\ldots a_{k+T}}',
            description: 'Every rational number has either terminating or repeating decimal expansion',
        },
        {
            id: 'terminating-condition',
            name: 'Terminating Condition',
            latex: '\\text{Terminating iff } q = 2^a5^b',
            description: 'A fraction terminates iff denominator has only 2 and 5 as prime factors',
        },
    ],
    derivatives: (state, params) => {
        const rateP = getParam(params, 'rateP');
        const rateQ = getParam(params, 'rateQ');
        const currentP = getState(state, 'numerator');
        const currentQ = getState(state, 'denominator');
        const nextP = Math.max(1, Math.min(50, currentP + rateP * 0.016));
        const nextQ = Math.max(2, Math.min(100, currentQ + rateQ * 0.016));
        const decimal = nextP / nextQ;
        // Calculate period length
        let q = nextQ;
        while (q % 2 === 0)
            q /= 2;
        while (q % 5 === 0)
            q /= 5;
        let period = 0;
        if (q > 1) {
            let rem = 1;
            const seen = new Map();
            while (!seen.has(rem)) {
                seen.set(rem, period);
                rem = (rem * 10) % q;
                period++;
                if (rem === 0) {
                    period = 0;
                    break;
                }
            }
        }
        const isTerminating = period === 0 ? 1 : 0;
        return {
            numerator: nextP - currentP,
            denominator: nextQ - currentQ,
            decimal: decimal - getState(state, 'decimal'),
            period: period - getState(state, 'period'),
            isTerminating: isTerminating - getState(state, 'isTerminating'),
        };
    },
    bounds: {
        numerator: { min: 1, max: 50 },
        denominator: { min: 2, max: 100 },
    },
});
// ============================================================================
// PERFECT NUMBER INVESTIGATION
// ============================================================================
export const PerfectNumberInvestigation = createExperimentFromSchema({
    id: 'perfect-number-investigation',
    name: 'Perfect Number Investigation',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Search for and analyze perfect numbers, investigating the relationship between a number and the sum of its proper divisors to classify numbers as perfect, abundant, or deficient.',
    conceptTags: ['Perfect Numbers', 'Divisors', 'Number Theory', 'Mersenne Primes'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'n', name: 'Number', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'sumProper', name: 'Sum of Proper Divisors', unit: 'dimensionless', initial: 0, symbol: 's(n)' },
        { id: 'abundance', name: 'Abundance', unit: 'dimensionless', initial: -1, symbol: 'a(n)', description: 's(n) - n' },
        { id: 'isPerfect', name: 'Is Perfect', unit: 'dimensionless', initial: 0, symbol: 'P' },
        { id: 'isAbundant', name: 'Is Abundant', unit: 'dimensionless', initial: 0, symbol: 'A' },
        { id: 'isDeficient', name: 'Is Deficient', unit: 'dimensionless', initial: 1, symbol: 'D' },
    ],
    parameters: [
        { id: 'maxN', name: 'Maximum Number', unit: 'dimensionless', min: 10, max: 1000, default: 100, symbol: 'N_{max}' },
        { id: 'step', name: 'Step Size', unit: 'dimensionless', min: 1, max: 10, default: 1, symbol: '\\Delta' },
    ],
    equations: [
        {
            id: 'perfect-definition',
            name: 'Perfect Number Definition',
            latex: 's(n) = n \\iff n \\text{ is perfect}',
            description: 'A perfect number equals the sum of its proper divisors',
        },
        {
            id: 'euclid-euler',
            name: 'Euclid-Euler Theorem',
            latex: 'n = 2^{p-1}(2^p-1) \\text{ is perfect when } 2^p-1 \\text{ is prime}',
            description: 'Characterizes even perfect numbers using Mersenne primes',
        },
    ],
    derivatives: (state, params) => {
        const n = getState(state, 'n');
        const maxN = getParam(params, 'maxN');
        const step = getParam(params, 'step');
        const nextN = Math.min(n + step, maxN);
        let sumProper = 0;
        for (let i = 1; i < nextN; i++) {
            if (nextN % i === 0) {
                sumProper += i;
            }
        }
        const abundance = sumProper - nextN;
        const isPerfect = abundance === 0 ? 1 : 0;
        const isAbundant = abundance > 0 ? 1 : 0;
        const isDeficient = abundance < 0 ? 1 : 0;
        return {
            n: nextN - n,
            sumProper: sumProper - getState(state, 'sumProper'),
            abundance: abundance - getState(state, 'abundance'),
            isPerfect: isPerfect - getState(state, 'isPerfect'),
            isAbundant: isAbundant - getState(state, 'isAbundant'),
            isDeficient: isDeficient - getState(state, 'isDeficient'),
        };
    },
    bounds: {
        n: { min: 1, max: 10000 },
        sumProper: { min: 0, max: 50000 },
    },
});
// ============================================================================
// FACTORIAL GROWTH ANALYSIS
// ============================================================================
export const FactorialGrowthAnalysis = createExperimentFromSchema({
    id: 'factorial-growth-analysis',
    name: 'Factorial Growth Analysis',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Compare factorial growth against exponential functions and analyze Stirling\'s approximation accuracy as n increases.',
    conceptTags: ['Factorial', 'Gamma Function', 'Stirling\'s Approximation', 'Growth Rates'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'n', name: 'Number', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'fact', name: 'Factorial', unit: 'dimensionless', initial: 1, symbol: 'n!', description: 'n factorial' },
        { id: 'stirling', name: 'Stirling Approx', unit: 'dimensionless', initial: 0.92, symbol: 'S(n)', description: 'Stirling approximation' },
        { id: 'ratio', name: 'n!/S(n)', unit: 'dimensionless', initial: 1.08, symbol: '\\rho' },
        { id: 'logFact', name: 'Log Factorial', unit: 'dimensionless', initial: 0, symbol: '\\ln(n!)' },
        { id: 'doubleFact', name: 'Double Factorial', unit: 'dimensionless', initial: 1, symbol: 'n!!' },
    ],
    parameters: [
        { id: 'maxN', name: 'Maximum N', unit: 'dimensionless', min: 2, max: 20, default: 10, symbol: 'N_{max}' },
        { id: 'step', name: 'Step Size', unit: 'dimensionless', min: 0.1, max: 1, default: 0.1, symbol: '\\Delta' },
    ],
    equations: [
        {
            id: 'stirling',
            name: 'Stirling\'s Approximation',
            latex: 'n! \\approx \\sqrt{2\\pi n}\\left(\\frac{n}{e}\\right)^n',
            description: 'Asymptotic approximation for factorial',
        },
        {
            id: 'factorial-def',
            name: 'Factorial Definition',
            latex: 'n! = \\prod_{k=1}^n k',
            description: 'Product of all positive integers up to n',
        },
    ],
    derivatives: (state, params) => {
        const n = getState(state, 'n');
        const maxN = getParam(params, 'maxN');
        const step = getParam(params, 'step');
        const nextN = Math.min(n + step, maxN);
        const intN = Math.floor(nextN);
        const fact = factorial(intN);
        // Stirling's approximation
        const stirling = Math.sqrt(2 * Math.PI * nextN) * Math.pow(nextN / Math.E, nextN);
        const ratio = stirling > 0 ? fact / stirling : 0;
        const logFact = Math.log(Math.max(1, fact));
        // Double factorial
        let doubleFact = 1;
        for (let i = intN; i > 0; i -= 2) {
            doubleFact *= i;
        }
        return {
            n: nextN - n,
            fact: fact - getState(state, 'fact'),
            stirling: stirling - getState(state, 'stirling'),
            ratio: ratio - getState(state, 'ratio'),
            logFact: logFact - getState(state, 'logFact'),
            doubleFact: doubleFact - getState(state, 'doubleFact'),
        };
    },
    bounds: {
        n: { min: 1, max: 20 },
        fact: { min: 1, max: 2.5e18 },
    },
});
// ============================================================================
// BASE CONVERSION PATTERNS
// ============================================================================
export const BaseConversionPatterns = createExperimentFromSchema({
    id: 'base-conversion-patterns',
    name: 'Base Conversion Patterns',
    domain: 'mathematics',
    category: 'number-systems',
    description: 'Study number representation across different bases, analyzing how the same numerical value appears in binary, octal, decimal, and hexadecimal systems.',
    conceptTags: ['Number Systems', 'Base Conversion', 'Positional Notation', 'Number Theory'],
    difficulty: 'beginner',
    stateVariables: [
        { id: 'decimal', name: 'Decimal Value', unit: 'dimensionless', initial: 10, symbol: 'N_{10}' },
        { id: 'binary', name: 'Binary Value', unit: 'dimensionless', initial: 2, symbol: 'N_2', description: 'Interpreted in base 10' },
        { id: 'octal', name: 'Octal Value', unit: 'dimensionless', initial: 8, symbol: 'N_8', description: 'Interpreted in base 10' },
        { id: 'hex', name: 'Hex Value', unit: 'dimensionless', initial: 16, symbol: 'N_{16}', description: 'Interpreted in base 10' },
        { id: 'digitSum', name: 'Sum of Digits (base 10)', unit: 'dimensionless', initial: 1, symbol: 'S_{10}(N)' },
        { id: 'bitCount', name: 'Number of Bits', unit: 'dimensionless', initial: 4, symbol: 'B_2(N)' },
    ],
    parameters: [
        { id: 'value', name: 'Target Value', unit: 'dimensionless', min: 1, max: 255, default: 10, symbol: 'N' },
        { id: 'rate', name: 'Rate of Change', unit: 'dimensionless/s', min: -5, max: 5, default: 1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'base-conversion',
            name: 'Base Conversion Formula',
            latex: 'N = \\sum_{i=0}^{k} d_i \\cdot b^i',
            description: 'Number equals sum of digits times powers of base',
        },
        {
            id: 'binary-length',
            name: 'Binary Length',
            latex: 'L = \\lfloor \\log_2(N) \\rfloor + 1',
            description: 'Number of bits needed to represent N',
        },
    ],
    derivatives: (state, params) => {
        const rate = getParam(params, 'rate');
        const currentDecimal = getState(state, 'decimal');
        const nextDecimal = Math.max(1, Math.min(255, currentDecimal + rate * 0.016));
        const intN = Math.floor(nextDecimal);
        const binaryStr = intN.toString(2);
        const binaryVal = parseInt(binaryStr, 10);
        const octalStr = intN.toString(8);
        const octalVal = parseInt(octalStr, 10);
        const hexStr = intN.toString(16).toUpperCase();
        const hexVal = parseInt(hexStr, 10);
        // Sum of decimal digits
        const decimalStr = intN.toString();
        let digitSum = 0;
        for (const char of decimalStr) {
            digitSum += parseInt(char, 10);
        }
        const bitCount = binaryStr.length;
        return {
            decimal: nextDecimal - currentDecimal,
            binary: binaryVal - getState(state, 'binary'),
            octal: octalVal - getState(state, 'octal'),
            hex: hexVal - getState(state, 'hex'),
            digitSum: digitSum - getState(state, 'digitSum'),
            bitCount: bitCount - getState(state, 'bitCount'),
        };
    },
    bounds: {
        decimal: { min: 1, max: 255 },
        binary: { min: 1, max: 11111111 },
        octal: { min: 1, max: 377 },
    },
});
// ============================================================================
// EXPORT ALL EXPERIMENTS
// ============================================================================
export const ALL_NUMBER_SYSTEMS = [
    PrimeDistributionExplorer,
    DivisibilityPatternInvestigator,
    GCDLCMRelationshipLab,
    ModularArithmeticCycles,
    DecimalExpansionBehavior,
    PerfectNumberInvestigation,
    FactorialGrowthAnalysis,
    BaseConversionPatterns,
];
//# sourceMappingURL=number-systems.js.map